import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { LockOutlined, QuestionOutlined } from '@ant-design/icons';
import { Menu } from 'antd';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import DynamicMenu, { DynamicMenuProps } from './components/DynamicMenu';
import SideMenu from './components/SideMenu';
import TabContent from './components/TabContent';
import styles from './Nav.module.scss';
import { TabType } from './types';
import {
  DynamicTabs,
  MenuTab,
} from '../../../../app/providers/redux/slices/app/types';
import { fetchTags } from '../../../../app/providers/redux/slices/reports/params/reports/reportsSlice';
import { ROLES } from '../../../../helpers/utils/auth';
import { showInfoNotification } from '../../../../helpers/utils/ui';
import { tgApi } from '../../api/endpoints/tgApi';
import {
  QUERY_FROM_MENU,
  QUERY_NAME_DASHBOARD,
} from '../../constants/query-params';
import { routeNames } from '../../constants/routes';
import { useActions } from '../../hooks/useActions';
import { useTypedSelector, useAppDispatch } from '../../hooks/useTypedSelector';

const pathToKeyMap: [string, MenuTab][] = [
  [ routeNames.schoolStaff, 'access' ],
  [ routeNames.adsSpend, 'ads' ],
  [ routeNames.adsConnect, 'ads' ],
  [ routeNames.orderReports, 'reports' ],
  [ routeNames.paymentReports, 'reports' ],
  [ routeNames.adReports, 'reports' ],
  [ routeNames.webinarReports, 'reports' ],
  [ routeNames.yandexMetrics, 'tools' ],
  [ routeNames.schoolEdit, 'settings' ],
  [ routeNames.schoolLoadData, 'settings' ],
  [ routeNames.schoolTrackScript, 'settings' ],
  [ routeNames.export, 'import' ],
  [ routeNames.partner, 'suppliers' ],
  [ routeNames.dashboards, 'dashboards' ],
  [ routeNames.home, 'reports' ],
];

const isDynamicTab = (tab: MenuTab): tab is DynamicTabs =>
  tab === 'access' ||
  tab === 'ads' ||
  tab === 'bd' ||
  tab === 'import' ||
  tab === 'settings' ||
  tab === 'tools';
const getBdLinks = (activeTab: TabType) => {
  switch (activeTab) {
    case 'webinars':
      return [
        {
          key: 'webinarList',
          label: 'Список вебинаров',
          url: routeNames.webinarList,
          permission: [ ROLES.userSettings, ROLES.admin ],
        },
        {
          key: 'reportWebinarComments',
          label: 'Комментарии',
          url: routeNames.reportWebinarComments,
          permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
        },
      ];
    case 'visits':
      return [
        {
          key: 'reportSession',
          label: 'Сессии',
          url: routeNames.reportSession,
          permission: [ ROLES.userSettings, ROLES.admin ],
        },
        {
          key: 'reportHit',
          label: 'Хиты',
          url: routeNames.reportHit,
          permission: [ ROLES.userSettings, ROLES.admin ],
        },
        {
          key: 'reportVisitors',
          label: 'Посетители',
          url: routeNames.reportVisitors,
          permission: [ ROLES.userSettings, ROLES.admin ],
        },
        {
          key: 'reportHitStat',
          label: 'Статистика по экранам',
          url: routeNames.reportHitStat,
          permission: [ ROLES.userSettings, ROLES.admin ],
        },
      ];
    case 'ads':
      return [
        {
          key: 'adsConnect',
          label: 'Кабинеты автоматического размещения',
          url: routeNames.adsConnect,
        },
        {
          key: 'adsConnect?T2',
          label: 'Объявления автоматического размещения',
          url: `${routeNames.adsConnect}?tab=2`,
        },
        {
          key: 'adsSpend',
          label: 'Кабинеты ручного размещения',
          url: routeNames.adsSpend,
        },
        {
          key: 'adsSpend?T2',
          label: 'Объявления ручного размещения',
          url: `${routeNames.adsSpend}?tab=2`,
        },
      ];   
    case 'platform':
    default:
      return [
        {
          key: 'Users',
          label: 'Пользователи',
          url: routeNames.reportUsers,
          permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
        },
        {
          key: 'reportRegs',
          label: 'Регистрации',
          url: routeNames.reportRegs,
          permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
        },
        {
          key: 'reportOrders',
          label: 'Заказы',
          url: routeNames.reportOrders,
          permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
        },
        {
          key: 'reportPayments',
          label: 'Платежи',
          url: routeNames.reportPayments,
          permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
        },
        {
          key: 'export',
          label: 'Экспорт',
          url: routeNames.export,
          permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
        },
      ];
  }
};

const getToolsLinks = (activeTab: TabType) => {
  switch (activeTab) {
    case 'integrations':
      return [
        {
          key: 'schoolAdd',
          label: 'GetCourse',
          url: routeNames.schoolAdd,
        },
        {
          key: 'adsConnect',
          label: 'Рекламные кабинеты',
          url: routeNames.adsConnect,
        },
        {
          key: 'yandexMetrics',
          label: 'Яндекс метрика',
          url: routeNames.yandexMetrics,
        },
        {
          key: 'webinarConnectBizon',
          label: 'Бизон 365',
          url: routeNames.webinarConnectBizon,
          permission: [ ROLES.userSettings, ROLES.admin  ],
        },
        {
          key: 'amoCRM',
          label: 'Амо CRM (скоро)',
          url: '',
          permission: [ ROLES.admin, ROLES.userSettings ],
        },
        {
          key: 'googleTable',
          label: 'Гугл таблица (скоро)',
          url: '',
        },
      ];
    default:
      return null;
  }
};

const HelpWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <span
    style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
  >
    <QuestionOutlined
      size={10}
      style={{
        background: 'red',
        color: 'white',
        borderRadius: '50%',
        padding: 5,
        fontSize: 12,
      }}
    />
    {children}
  </span>
);
const style = {
  border: '1px solid red',
  borderRight: 'none',
  borderTopLeftRadius: '30px',
  borderBottomLeftRadius: '30px',
  color: 'red',
  backgroundColor: 'white',
};

const Nav: FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { getSavedReports, setActiveMainTab, setTGUsers } = useActions();
  const { savedReports } = useTypedSelector((state) => state.reports);
  const { savedDashboards } = useTypedSelector((state) => state.reports);
  const { activeMainTab } = useTypedSelector((state) => state.app);
  const mainHash = useTypedSelector((state) => state.reports.mainDashboardHash);

  const [ isSideMenuOpen, setIsSideMenuOpen ] = useState(false);
  const [ activeTab, setActiveTab ] = useState<TabType>('all');
  const [ activeContentTab, setActiveContentTab ] = useState<
    'all' | 'my' | 'schools' | 'scan'
  >('all');
  const [ dynamicOpen, setDynamicOpen ] = useState(false);

  const dispatch = useAppDispatch();
  const { tags } = useTypedSelector((state) => state.reports);
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { setAppReportOpen, setDrawerLink } = useActions();
  const navRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();

  const getTGusersEffect = () => {
    const fetchUsers = async () => {
       const users = await tgApi.getAvailableUsers();
       if (Array.isArray(users)) {
         const newUsers = users.map((user) => ({
           value: user.id,
           label: user.name,
         }));
         setTGUsers(newUsers);
       }
     };
     fetchUsers();
};
useEffect(getTGusersEffect, []);
  useEffect(() => {
    const matchedEntry = pathToKeyMap.find(([ path ]) =>
      location.pathname.startsWith(path)
    );
    if (
      location.pathname.startsWith(routeNames.adsConnect) ||
      location.pathname.startsWith(routeNames.adsSpend)
    ) {
      setActiveTab('all');
      closeAdMenu();
    }

    if (matchedEntry) {
      setActiveMainTab(matchedEntry[1]);
      closeAllMenus();
    }

    if (
      searchParams.get(QUERY_NAME_DASHBOARD) &&
      location.pathname.startsWith(routeNames.dashboards)
    ) {
      const fromMenu = searchParams.get(QUERY_FROM_MENU);
      setActiveMainTab(fromMenu ? 'overview' : 'dashboards');
    }
  }, [ location.pathname ]);

  useEffect(() => {
    if (selectedSchool?.id) {
      getSavedReports(selectedSchool.id);
    }
  }, [ selectedSchool?.id ]);

  useEffect(() => {
    dispatch(fetchTags());
  }, [ dispatch ]);

  useEffect(() => {
    setActiveContentTab('all');
  }, []);

  const handleOpenMainDashboard = () => {
    if (mainHash) {
      const query = new URLSearchParams([
        [ QUERY_NAME_DASHBOARD, mainHash ],
        [ QUERY_FROM_MENU, 'true' ],
      ]);
      const location = `${routeNames.dashboards}?${query.toString()}`;
      navigate(location);
      closeAllMenus();
    } else {
      showInfoNotification('Добавьте дашборд в сводку');
    }
  };
  const handleOpenHelpFrame = (url: string) => {
    setDrawerLink(`${url}?timestamp=${new Date().getTime()}`);
    setAppReportOpen(true);
    closeAllMenus();
  };
  const closeAllMenus = () => {
    setDynamicOpen(false);
    setIsSideMenuOpen(false);
  };
  const handleMainTabClick = (tab: typeof activeMainTab) => {
    if (activeMainTab === tab) {
      setIsSideMenuOpen((prev) => !prev);
    } else {
      closeAllMenus();
      setActiveMainTab(tab);
      setIsSideMenuOpen(true);
    }
  };

  const handleMenuSelect = (
    tab: MenuTab,
    onChangeVisible?: (visible: boolean) => void
  ) => {
    if (activeMainTab !== tab && dynamicOpen) return;

    setDynamicOpen((prev) => {
      onChangeVisible?.(prev);
      return !prev;
    });
  };

  const handleAdTabClick = () => {
    setIsSideMenuOpen(false);
    handleMenuSelect('ads');
  };
  const handleBdTabClick = () => {
    setIsSideMenuOpen(false);
    
    setActiveTab('platform');
    handleMenuSelect('bd');
  };
  const handleAccessTabClick = () => {
    setIsSideMenuOpen(false);
    handleMenuSelect('access');
  };
  const handleToolsTabClick = () => {
    setIsSideMenuOpen(false);
    handleMenuSelect('tools');
  };
  const handleSettingsTabClick = () => {
    setIsSideMenuOpen(false);
    setActiveTab('integrations');
    handleMenuSelect('settings');
  };
  const closeSideMenu = () => {
    setIsSideMenuOpen(false);
  };
  const closeAdMenu = () => {
    setDynamicOpen(false);
    setActiveTab('all');
  };

  const transformedData =
    savedReports?.map((report) => ({
      id: report.id,
      name: report.name || 'Без названия',
      hash: report.hash,
      type: report.access_id,
      report_id: report.report_id,
      tags: report.tags
        ? Array.isArray(report.tags)
          ? report.tags.map((t: any) => ({
              id: Number(t.id),
              name: String(t.name),
            }))
          : Object.entries(report.tags).map(([ id, name ]) => ({
              id: Number(id),
              name: String(name),
            }))
        : [],
      category: (() => {
        switch (report.report_id) {
          case 1:
            return 'registrations';
          case 2:
            return 'orders';
          case 3:
            return 'payments';
          case 4:
            return 'ads';
          case 5:
            return 'webinars';
          default:
            return 'registrations';
        }
      })(),
      is_favorite: report.is_favorite,
      values: report.values || {},
    })) || [];

  const transformedDashboardsData =
    savedDashboards?.map((dashboard) => {
      const parseTags = (tags: any) => {
        if (Array.isArray(tags)) {
          return tags.map((t) => ({ id: t.id, name: t.name }));
        }
        if (typeof tags === 'object' && !Array.isArray(tags)) {
          return Object.entries(tags).map(([ id, name ]) => ({
            id: Number(id),
            name: String(name),
          }));
        }
        return [];
      };

      return {
        id: dashboard.id,
        name: dashboard.name || 'Без названия',
        // type: dashboard.access_id === 2 ? 'школа' : 'my',
        type:
          dashboard.access_id === 2
            ? 'школа'
            : dashboard.access_id === 3
            ? 'SCAN'
            : 'my',
        tags: parseTags(dashboard.tags),
        is_favorite: dashboard.is_favorite,
        report_id: null, // Добавляем report_id
        hash: null, // Добавляем hash
        values: dashboard.values || {},
      };
    }) || [];

  const items: (Omit<MenuItemType, 'key'> & { key: MenuTab })[] = [
    {
      key: 'overview',
      label: (
        <div
          style={{
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          }}
          onClick={handleOpenMainDashboard}
        >
          Сводка
        </div>
      ),
      icon: (
        <img style={{ width: '20px' }} alt="overview" src="/overview.png" />
      ),
    },
    {
      key: 'dashboards',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={() => handleMainTabClick('dashboards')}
        >
          Дашборды
        </div>
      ),
      icon: (
        <img style={{ width: '20px' }} alt="overview" src="/dashboard.png" />
      ),
    },
    {
      key: 'reports',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={() => handleMainTabClick('reports')}
        >
          Отчёты
        </div>
      ),
      icon: <img style={{ width: '20px' }} alt="overview" src="/chart.png" />,
    },
    {
      key: 'ads',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={handleAdTabClick}
        >
          Реклама
        </div>
      ),
      icon: <img style={{ width: '20px' }} alt="overview" src="/ads.png" />,
    },
    {
      key: 'bd',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={handleBdTabClick}
        >
          База данных
        </div>
      ),
      icon: <img style={{ width: '20px' }} alt="overview" src="/bd.png" />,
    },
    {
      key: 'access',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={handleAccessTabClick}
        >
          Доступы
        </div>
      ),
      icon: <img style={{ width: '20px' }} alt="overview" src="/access.png" />,
    },
    {
      key: 'tools',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={handleToolsTabClick}
        >
          Инструменты
        </div>
      ),
      icon: <img style={{ width: '20px' }} alt="overview" src="/tools.png" />,
    },
    {
      key: 'settings',
      label: (
        <div
          style={{ cursor: 'pointer', fontSize: '14px' }}
          onClick={handleSettingsTabClick}
        >
          Настройка
        </div>
      ),
      icon: (
        <img style={{ width: '20px' }} alt="overview" src="/settings.png" />
      ),
    },
    {
      key: 'suppliers',
      label: (
        <Link
          style={{ cursor: 'pointer', fontSize: '14px' }}
          to={routeNames.partner}
        >
          Партнёрка
        </Link>
      ),
      icon: <img style={{ width: '20px' }} alt="overview" src="/suppl.png" />,
    },
    {
      key: 'education',
      label: (
        <div style={{ cursor: 'pointer', fontSize: '14px', color: '#999999' }} onClick={() => {}}>
          {'Обучение (Скоро)'}
        </div>
      ),
      icon: <LockOutlined width={20} height={20} style={{fontSize: 20, color: '#999999'}}/>,
    },
  ];

  const adMenuItems = [
    {
      key: 'ad-accounts-helper',
      label: (
        <HelpWrapper>
          Как настроить рекламу для сквозной аналитики?
        </HelpWrapper>
      ),
      style,
      onClick: () =>
        handleOpenHelpFrame(
          'https://help.scananalytics.ru/nastroyka-reklamy-dlya-skvoznoy-analitiki/'
        ),
    },
    {
      key: 'ad-accounts',
      label: 'Подключение рекламных кабинетов',
      url: routeNames.adsConnect,
    },
    {
      key: 'auto-placement-accounts',
      label: 'Кабинеты автоматического размещения',
      url: `${routeNames.adsConnect}?tab=1`,
    },
    {
      key: 'auto-placement-ads',
      label: 'Объявления автоматического размещения',
      url: `${routeNames.adsConnect}?tab=2`,
    },
    {
      key: 'manual-placement-accounts',
      label: 'Кабинеты ручного размещения',
      url: `${routeNames.adsSpend}?tab=1`,
    },
    {
      key: 'manual-placement-ads',
      label: 'Объявления ручного размещения',
      url: `${routeNames.adsSpend}?tab=2`,
    },
    {
      key: 'add-ad-spend',
      label: 'Добавить рекламный расход',
      url: `${routeNames.adsSpend}?tab=2&showModal=true`,
    },
  ];

  const bdMenuItems = [
    {
      key: 'platform',
      label: 'Платформа',
      onClick: () =>
        setActiveTab('platform'),
      withoutClose: true,
    },
    {
      key: 'webinars',
      label: 'Вебинары',
      onClick: () =>
        setActiveTab('webinars'),
      withoutClose: true,
    },
    {
      key: 'visits',
      label: 'Посещения',
      onClick: () =>
        setActiveTab('visits'),
      withoutClose: true,
    },
    {
      key: 'ads',
      label: 'Реклама',
      onClick: () =>
        setActiveTab('ads'),
      withoutClose: true,
    },
  ];

  const accessMenuItems = [
    {
      key: 'access-settings-helper',
      label: <HelpWrapper>Как настроить доступ сотрудникам?</HelpWrapper>,
      style,
      onClick: () =>
        handleOpenHelpFrame('https://help.scananalytics.ru/sotrudniki/'),
    },
    {
      key: 'employees',
      label: 'Сотрудники',
      url: routeNames.schoolStaff,
      permission: [ ROLES.userSettings, ROLES.admin ],
    },
    {
      key: 'employee-activity',
      label: 'Активность сотрудников (скоро)',
      url: '',
    },
  ];

  const toolsMenuItems = [
    {
      key: 'yandex-metric',
      label: 'Отправка конверсий в Яндекс.Метрику',
      url: `${routeNames.yandexMetrics}?tab=2`,
    },
    {
      key: 'reportTg',
      label: 'Отправка отчётов в Telegram. Радар',
      onClick: () =>
        handleOpenHelpFrame(
          'https://help.scananalytics.ru/otpravka-lyubyh-otchetov-v-telegram-v-opredelyonnoe-vremya-ruka-na-pulse/'
        ),
    },
    {
      key: 'analytics',
      label: 'Робот аналитик (скоро)',
      url: '',
    },
    {
      key: 'directolog',
      label: 'Робот директолог (скоро)',
      url: '',
    },
    {
      key: 'addSchool',
      label: 'Подключить новую школу',
      url: routeNames.schoolAdd,
    },
  ];

  const settingsMenuItems = [
    {
      key: 'integrations-how-work',
      label: <HelpWrapper>Как настроить сервис?</HelpWrapper>,
      style,
      onClick: () => {
        handleOpenHelpFrame('https://help.scananalytics.ru/etapy-nastroyki-analitiki/');
      },
    },
    {
      key: 'integrations',
      label: 'Интеграции',
      onClick: () => {
        setActiveTab('integrations');
      },
    },
    {
      key: 'school-info',
      label: 'Информация о школе',
      url: routeNames.schoolEdit,
      permission: [ ROLES.userSettings, ROLES.admin ],
    },

    {
      key: 'sell',
      label: 'Воронки продаж',
      onClick: () =>
        handleOpenHelpFrame('https://help.scananalytics.ru/voronki-prodazh/'),
    },
    {
      key: 'add-data',
      label: 'Ручная загрузка данных по школе',
      url: routeNames.schoolLoadData,
      permission: [ ROLES.userSettings, ROLES.userContacts, ROLES.admin ],
    },
    {
      key: 'tracking',
      label: 'Скрипт отслеживания',
      url: routeNames.schoolTrackScript,
      permission: [ ROLES.userSettings, ROLES.admin ],
    },
    {
      key: 'utm',
      label: 'Скрипт захвата UTM меток',
      onClick: () =>
        handleOpenHelpFrame(
          'https://help.scananalytics.ru/skript-vstavki-utm-metok-iz-url-v-dop-polya-nulevogo-zakaza-gc/'
        ),
    },
  ];


  const baseDynamicProps = {
    activeMainTab,
    activeTab,
    setActiveTab,
    collapsed,
  };

  const dynamicsProps: Record<
    DynamicTabs,
    Omit<
      DynamicMenuProps,
      'activeMainTab' | 'activeTab' | 'setActiveTab' | 'collapsed' | 'isOpen'
    >
  > = {
    ads: {
      items: adMenuItems,
      onClose: closeAdMenu,
    },
    bd: {
      items: bdMenuItems,
      onClose: () => setDynamicOpen(false),
      nextLevel: {
        items: getBdLinks(activeTab) ?? undefined,
      },
    },
    access: {
      items: accessMenuItems,
      onClose: () => setDynamicOpen(false),
    },
    tools: {
      items: toolsMenuItems,
      onClose: () => setDynamicOpen(false),
    },
    settings: {
      items: settingsMenuItems,
      onClose: () => setDynamicOpen(false),
      nextLevel: {
        items: getToolsLinks(activeTab) ?? undefined,
      },
    },
  };
  const Dynamic = isDynamicTab(activeMainTab) ? (
    <DynamicMenu
      isOpen={dynamicOpen}
      {...dynamicsProps[activeMainTab]}
      {...baseDynamicProps}
    />
  ) : null;

  return (
    <div className={styles.siderChildren} ref={navRef}>
      <div className={styles.navMenu}>
        <Menu
          mode="inline"
          className={styles.navTabs}
          items={items}
          selectedKeys={[ activeMainTab ]}
          onSelect={({ key }) => {
            if(key === 'education') return;
            if (key === 'dashboards' || key === 'reports') setActiveTab('all');
            setActiveMainTab(key as typeof activeMainTab);
          }}
          style={{
            paddingLeft: '10px',
            paddingRight: '0px',
            marginTop: '1px',
          }}
          inlineCollapsed={collapsed}
        />
      </div>
      {Dynamic}
      <SideMenu
        isOpen={isSideMenuOpen}
        onClose={closeSideMenu}
        onOpenHelpFrame={handleOpenHelpFrame}
        tags={tags}
        {...baseDynamicProps}
      />
      {isSideMenuOpen && (
        <TabContent
          mainTab={activeMainTab}
          subTab={
            activeTab === 'all' || activeTab === 'favorites' ? activeTab : 'all'
          }
          activeContentTab={activeContentTab}
          onContentTabChange={setActiveContentTab}
          activeMainTab={activeMainTab}
          data={
            activeMainTab === 'reports'
              ? transformedData
              : transformedDashboardsData
          }
          activeReportTab={
            activeTab === 'registrations' ||
            activeTab === 'orders' ||
            activeTab === 'payments' ||
            activeTab === 'webinars' ||
            activeTab === 'ads'
              ? activeTab
              : undefined
          }
          activeTagTab={
            activeTab === 'manager' ||
            activeTab === 'traffic' ||
            activeTab === 'funnels' ||
            activeTab === 'contractors'
              ? activeTab
              : undefined
          }
          activeTab={activeTab}
          tags={tags}
          onItemClick={() => {
            closeSideMenu();
          }}
          collapsed={collapsed}
        />
      )}
    </div>
  );
};

export default Nav;
